import React, { useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import { useNotify } from "react-admin";

// Constants
import { PASSWORD_RULES_HINT, getPasswordStrengthError } from "../../constants/passwordRules";

// Providers
import { adminsProvider } from "../../providers/admins";

const ChangeMyPasswordButton: React.FC = () => {
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setOpen(false);
    setCurrentPassword("");
    setNewPassword("");
    setError(null);
  };

  const handleSubmit = async () => {
    if (!currentPassword) {
      setError("Current password is required");
      return;
    }

    const validationError = getPasswordStrengthError(newPassword);

    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);

    try {
      await adminsProvider.changePassword(currentPassword, newPassword);
      notify("Password changed successfully", { type: "success" });
      handleClose();
    } catch (err: any) {
      notify(err?.message || "Failed to change password", { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button variant="outlined" onClick={() => setOpen(true)}>
        Change My Password
      </Button>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
        <DialogTitle>Change my password</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 1 }}>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Current password"
            type="password"
            value={currentPassword}
            onChange={(e) => {
              setCurrentPassword(e.target.value);
              setError(null);
            }}
          />
          <TextField
            fullWidth
            margin="dense"
            label="New password"
            type="password"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setError(null);
            }}
            error={Boolean(error)}
            helperText={error ?? PASSWORD_RULES_HINT}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting} variant="contained">
            Change Password
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChangeMyPasswordButton;
